"use client";

import { Fragment } from "react";
import { Term } from "@/components/ui/atoms/term";
import {
  QUALITY_LABEL,
  entryFunctionalContext,
  intervalDegreeLabel,
  type ChordEntry,
} from "@/lib/music/chord-catalog";
import { useChordLabel, useNoteLabel } from "@/hooks/use-notation";
import { formatAccidentals } from "@/lib/music/theory-helpers";
import {
  BORDER,
  FONT_MONO,
  TEXT,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
  VIOLET_SOFT,
} from "./palette";

type Props = {
  entry: ChordEntry;
  inversionTitle: string;
  slashChord: string;
  bassName: string;
  inversionIndex: number;
  intervalsFromBass: { from: string; to: string; name: string }[];
};

export function ChordPracticeGridTheoryStrip({
  entry,
  inversionTitle,
  slashChord,
  bassName,
  inversionIndex,
  intervalsFromBass,
}: Props) {
  return (
    <section
      className="relative z-10 grid shrink-0"
      style={{
        padding: "14px 24px",
        borderBottom: `1px solid ${BORDER}`,
        background: "rgba(10, 10, 14, 0.6)",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 16,
      }}
    >
      <TheoryColumnIdentity
        symbol={entry.symbol}
        qualityName={QUALITY_LABEL[entry.quality]}
        formula={entry.intervals.map(intervalDegreeLabel).join(" · ")}
        context={entryFunctionalContext(entry)}
      />
      <TheoryColumnInversion
        title={inversionTitle}
        slashChord={slashChord}
        bassName={bassName}
        inversionIndex={inversionIndex}
      />
      <TheoryColumnIntervals intervals={intervalsFromBass} />
    </section>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 9,
        fontFamily: FONT_MONO,
        color: TEXT_MUTE,
        letterSpacing: "0.18em",
        fontWeight: 600,
        marginBottom: 6,
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

type IdentityProps = {
  symbol: string;
  qualityName: string;
  formula: string;
  context: ReturnType<typeof entryFunctionalContext>;
};

function TheoryColumnIdentity({
  symbol,
  qualityName,
  formula,
  context,
}: IdentityProps) {
  const chordLabel = useChordLabel();
  const toNoteLabel = useNoteLabel();

  return (
    <div>
      <SectionLabel>Accord · {qualityName}</SectionLabel>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          marginBottom: 4,
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 300,
            color: TEXT,
            letterSpacing: "-0.02em",
          }}
        >
          {chordLabel(symbol)}
        </div>
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 11,
            color: VIOLET_BRIGHT,
            letterSpacing: "0.08em",
          }}
        >
          {formatAccidentals(formula)}
        </div>
      </div>
      <div style={{ fontSize: 11, color: TEXT_DIM, lineHeight: 1.5 }}>
        {context.length === 0 ? (
          <span>
            Pas de fonction tonale stable, utilisé comme accord de passage ou
            pour créer une tension chromatique.
          </span>
        ) : (
          context.map((c, i) => (
            <Fragment key={i}>
              {i > 0 && ", "}
              {i === 0 && "Degré "}
              <Term termKey="degre">{formatAccidentals(c.degree)}</Term>
              {" de "}
              {toNoteLabel(c.tonic)}{" "}
              {c.mode === "major" ? "majeur" : "mineur"}
            </Fragment>
          ))
        )}
        .
      </div>
    </div>
  );
}

type InversionProps = {
  title: string;
  slashChord: string;
  bassName: string;
  inversionIndex: number;
};

function TheoryColumnInversion({
  title,
  slashChord,
  bassName,
  inversionIndex,
}: InversionProps) {
  const chordLabel = useChordLabel();
  const toNoteLabel = useNoteLabel();

  return (
    <div style={{ paddingLeft: 16, borderLeft: `1px solid ${BORDER}` }}>
      <SectionLabel>Renversement · {title}</SectionLabel>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 4,
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 300,
            color: VIOLET_BRIGHT,
            letterSpacing: "-0.01em",
            lineHeight: 1,
          }}
        >
          {chordLabel(slashChord)}
        </div>
        <div
          style={{
            padding: "2px 7px",
            borderRadius: 4,
            background: VIOLET_SOFT,
            border: `1px solid ${VIOLET_GLOW}`,
            fontFamily: FONT_MONO,
            fontSize: 9,
            color: VIOLET_BRIGHT,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          Basse · {toNoteLabel(bassName)}
        </div>
      </div>
      <div style={{ fontSize: 11, color: TEXT_DIM, lineHeight: 1.5 }}>
        <InversionDescription index={inversionIndex} />
      </div>
    </div>
  );
}

function InversionDescription({ index }: { index: number }) {
  if (index === 0) {
    return (
      <>
        La <Term termKey="tonique">tonique</Term> est à la basse. Position la
        plus stable et la plus fréquente.
      </>
    );
  }

  if (index === 1) {
    return (
      <>
        La <Term termKey="tierce">tierce</Term> est à la basse, la{" "}
        <Term termKey="tonique">tonique</Term> est déplacée à l'
        <Term termKey="octave">octave</Term>. Sonore et ouvert, idéal pour les{" "}
        <Term termKey="voicing">voicings</Term> de chanson.
      </>
    );
  }

  return (
    <>
      La <Term termKey="quinte">quinte</Term> est à la basse. Sonorité instable
     , souvent utilisée dans la{" "}
      <Term termKey="quarteEtSixte">cadence à la quarte et sixte</Term>.
    </>
  );
}

type IntervalsProps = {
  intervals: { from: string; to: string; name: string }[];
};

function TheoryColumnIntervals({ intervals }: IntervalsProps) {
  const toNoteLabel = useNoteLabel();

  return (
    <div style={{ paddingLeft: 16, borderLeft: `1px solid ${BORDER}` }}>
      <SectionLabel>Intervalles depuis la basse</SectionLabel>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 11,
          color: TEXT,
          marginBottom: 6,
          letterSpacing: "0.04em",
          lineHeight: 1.5,
        }}
      >
        {intervals.map((iv, i) => (
          <div key={i} style={{ color: TEXT_DIM }}>
            {toNoteLabel(iv.from)}{" "}
            <span style={{ color: TEXT_MUTE }}>→</span>{" "}
            {toNoteLabel(iv.to)}{" "}
            <span style={{ color: VIOLET_BRIGHT, marginLeft: 4 }}>
              {iv.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
